import { Color } from "@shared/common/Themes/types";
import { useContext, useEffect, useMemo } from "react";
import { getBotValues } from "@shared/features/bots/BotForm/common/utils";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { useCompareBotValues } from "@shared/features/bots/BotForm/common/hooks";
import { useSymbolsMetadata, useUser } from "@shared/utils/hooks/misc";
import { BaseBotData, BotData } from "@shared/types";
import { AMOUNT_PERCENTAGE_BASE_FEATURE_FLAG } from "@shared/common/constants";

interface useFormValuesModifiedProps {
  isSelected?: boolean;
  valuesModified?: boolean;
  setValuesModified?: (valuesModified: boolean) => void;
  originalBotData?: BotData | BaseBotData;
}

const useFormValuesModified = ({
  isSelected,
  valuesModified,
  setValuesModified,
  originalBotData,
}: useFormValuesModifiedProps) => {
  const { botType, formValues, symbolsMetadata } =
    useContext(BotFormContext) || {};
  const { compareValues } = useCompareBotValues();
  const { getExchangeSymbols, getAccountActiveSymbols } = useSymbolsMetadata();
  const { user } = useUser();
  const hasAmountPercentageFeatureFlag = user?.hasFeatureFlag(
    AMOUNT_PERCENTAGE_BASE_FEATURE_FLAG
  );

  const borderColor: Color | undefined = useMemo(() => {
    if (!isSelected) return;
    if (valuesModified) return "gold";
    return "primaryBlue";
  }, [valuesModified, isSelected]);

  useEffect(() => {
    if (!isSelected || !originalBotData) return;
    const [
      originalBotValues,
      originalBotCheckedFields,
      originalBotOverrideFields,
    ] =
      originalBotData && botType
        ? getBotValues(botType, originalBotData, {
            getExchangeSymbols:
              symbolsMetadata?.getExchangeSymbols ?? getExchangeSymbols,
            getAccountActiveSymbols:
              symbolsMetadata?.getAccountActiveSymbols ??
              getAccountActiveSymbols,
            hasAmountPercentageFeatureFlag,
          })
        : [];
    setValuesModified?.(
      compareValues(
        undefined,
        originalBotValues,
        originalBotCheckedFields,
        originalBotOverrideFields,
        ["account", "name", "accounts", "subGroup"]
      )
    );
  }, [isSelected, formValues]);

  return { borderColor };
};

export default useFormValuesModified;
